import { ShoppingBag, ArrowRight, RefreshCw, CheckCircle2, Lock, Sparkles } from "lucide-react";
import { affiliateClick } from "../lib/api";

const RETAILERS = [
  { key: "amazon",  label: "Amazon",  color: "#FF9900", text: "#111111" },
  { key: "target",  label: "Target",  color: "#CC0000", text: "#ffffff" },
  { key: "sephora", label: "Sephora", color: "#000000", text: "#ffffff" },
  { key: "ulta",    label: "Ulta",    color: "#E91E8C", text: "#ffffff" },
];

const VERDICT_COPY = {
  AVOID: {
    header: "🔄 What to use instead for",
    subtext: "This product conflicts with your medication's label. Here's what to use instead:",
    tone: "text-rose-800",
    bg: "bg-rose-50/80 border-rose-100",
  },
  CAUTION: {
    header: "💡 What to use instead",
    subtext: "If you'd like a gentler choice, here's what to use instead.",
    tone: "text-amber-900",
    bg: "bg-amber-50/80 border-amber-100",
  },
  MOSTLY_SAFE: {
    header: "💡 What to use instead",
    subtext: "Small improvements available — here's what to use instead.",
    tone: "text-amber-900",
    bg: "bg-amber-50/80 border-amber-100",
  },
  SAFE: {
    header: "✅ Popular with other",
    subtext: "Other users on the same medication love these:",
    tone: "text-emerald-800",
    bg: "bg-emerald-50/80 border-emerald-100",
  },
};

function AttributePill({ label }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-surface-line text-[10.5px] font-semibold text-ink-soft">
      <CheckCircle2 className="w-2.5 h-2.5 text-safe-deep" />
      {label}
    </span>
  );
}

function SwapCard({ alt, index, onRecheck, medicationId, resultType }) {
  const isTopPick = index === 0;
  const price = typeof alt.price_usd === "number" ? alt.price_usd.toFixed(2) : null;
  const attributes = [];
  if (alt.fragrance_free) attributes.push("fragrance-free");
  if (alt.non_comedogenic) attributes.push("non-comedogenic");
  if (alt.vegan) attributes.push("vegan");
  if (alt.cruelty_free) attributes.push("cruelty-free");

  const activeRetailers = RETAILERS.filter((r) => alt[`affiliate_${r.key}`]);

  async function handleBuyClick(e, retailerKey) {
    e.preventDefault();
    // Pre-open a placeholder tab synchronously so popup blockers don't fire,
    // then redirect it once the POST resolves.
    const popup = window.open("about:blank", "_blank", "noopener,noreferrer");
    const resp = await affiliateClick({
      productId: alt.id,
      retailer: retailerKey,
      source: "result",
      medicationId,
      resultType,
    });
    const url = resp?.redirect_url;
    if (popup) {
      popup.location.href = url;
    } else if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <div
      data-testid={`swap-card-${alt.id}`}
      className="relative rounded-2xl bg-white border border-surface-line p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex flex-col"
    >
      {isTopPick && (
        <span
          data-testid={`swap-top-pick-${alt.id}`}
          className="absolute -top-2.5 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-white text-[10.5px] font-bold uppercase tracking-wide shadow-md"
        >
          <span aria-hidden>🏆</span>
          Top Pick
        </span>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-mute font-semibold">
            {alt.brand}
          </div>
          <div className="font-bold text-ink mt-1 leading-snug text-[15px]">
            {alt.name}
          </div>
          {price && (
            <div className="mt-1 text-[13px] text-ink-soft font-semibold">
              ${price}
            </div>
          )}
        </div>
      </div>

      {attributes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {attributes.map((a) => <AttributePill key={a} label={a} />)}
        </div>
      )}

      {alt.why_safer && (
        <p
          data-testid={`why-safer-${alt.id}`}
          className="mt-3 text-[12.5px] text-ink-soft leading-snug"
        >
          <span className="font-semibold text-ink">Why this one:</span> {alt.why_safer}
        </p>
      )}

      {(alt.sources || []).length > 0 && (
        <div
          data-testid={`swap-sources-${alt.id}`}
          className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1"
        >
          {(alt.sources || []).map((s, i) => (
            <a
              key={i} href={s.url} target="_blank" rel="noreferrer"
              className="text-[11.5px] text-safe-deep underline-offset-2 hover:underline"
            >
              {s.label}
            </a>
          ))}
        </div>
      )}

      {/* Retailer buy buttons — POST to /api/affiliate/click then window.open */}
      <div className="mt-4 space-y-2">
        {activeRetailers.length > 0 ? (
          activeRetailers.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={(e) => handleBuyClick(e, r.key)}
              data-testid={`swap-buy-${r.key}-${alt.id}`}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[13px] font-bold transition-all hover:scale-[1.02] active:scale-[0.98] hover:brightness-110"
              style={{ backgroundColor: r.color, color: r.text }}
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              Buy on {r.label}
              {price && <span className="opacity-90"> · ${price}</span>}
            </button>
          ))
        ) : (
          <div className="text-[11.5px] text-ink-mute italic">
            Ask your pharmacist or find it in-store.
          </div>
        )}
        {onRecheck && (
          <button
            type="button"
            data-testid={`swap-recheck-${alt.id}`}
            onClick={() => onRecheck(alt)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-cream-100 hover:bg-cream-200 text-ink border border-surface-line text-[12px] font-semibold transition-all"
          >
            <RefreshCw className="w-3 h-3" />
            Re-check this product
          </button>
        )}
      </div>
    </div>
  );
}

function LockedSwaps({ count, onUpgrade }) {
  const placeholders = Array.from({ length: Math.min(Math.max(count, 1), 3) });
  return (
    <div data-testid="swap-locked" className="relative mt-6">
      {/* Blurred teaser cards */}
      <div
        aria-hidden="true"
        className="grid gap-4 sm:grid-cols-3 pointer-events-none select-none blur-[6px] opacity-70"
      >
        {placeholders.map((_, i) => (
          <div
            key={i}
            className="rounded-2xl bg-white border border-surface-line p-5 flex flex-col"
          >
            <div className="h-3 w-16 rounded bg-cream-200" />
            <div className="mt-2 h-4 w-32 rounded bg-cream-200" />
            <div className="mt-2 h-3 w-12 rounded bg-cream-200" />
            <div className="mt-4 h-9 w-full rounded-xl bg-cream-200" />
          </div>
        ))}
      </div>

      {/* Unlock overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center bg-white/85 backdrop-blur-sm rounded-2xl border border-surface-line px-6 py-6 max-w-sm mx-4 shadow-subtle">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cream-100 border border-surface-line text-ink">
            <Lock className="w-4 h-4" />
          </span>
          <p className="mt-3 text-[15px] font-bold text-ink">
            {count} checked {count === 1 ? "alternative" : "alternatives"} ready
          </p>
          <p className="mt-1 text-[13px] text-ink-soft leading-relaxed">
            See exactly what to use instead — checked against your medication's
            label, with where to buy.
          </p>
          <button
            type="button"
            data-testid="swap-unlock-btn"
            onClick={onUpgrade}
            className="mt-4 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Sparkles className="w-4 h-4" />
            Unlock with Pro
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SafeSwapSection({ result, onRecheck, onPaywall }) {
  const list = result.safer_alternatives || [];
  const locked = !!result.safe_alternatives_locked;
  const lockedCount = result.safe_alternatives_count || 0;
  if (!list.length && !locked) return null;

  const verdict = result.verdict || "CAUTION";
  const copy = VERDICT_COPY[verdict] || VERDICT_COPY.CAUTION;
  const isSafe = verdict === "SAFE";
  const isAvoid = verdict === "AVOID";
  const shown = isSafe ? list.slice(0, 2) : list;
  const handleUpgrade = () => {
    if (onPaywall) onPaywall();
    else if (typeof window !== "undefined") window.location.assign("/pricing");
  };

  // Med name for "Safe Swaps for Accutane" / "Popular with other Accutane users"
  const firstMed = (result.medications && result.medications[0]) || result.medication;
  const medName = firstMed?.name || "your medication";
  const medicationId = firstMed?.id || null;

  return (
    <section
      data-testid="safe-swap-section"
      className={`rounded-3xl border p-6 sm:p-8 ${copy.bg}`}
    >
      <div className={`flex items-center gap-2 ${copy.tone}`}>
        <h3
          data-testid="swap-header"
          className="text-xl sm:text-2xl font-extrabold tracking-[-0.015em] leading-tight"
        >
          {copy.header}{" "}
          {(isAvoid || isSafe) && (
            <span className="font-editorial italic font-medium">
              {medName}
              {isSafe ? " users" : ""}
            </span>
          )}
        </h3>
      </div>
      <p className={`mt-2 text-[13.5px] ${copy.tone} opacity-90`}>
        {copy.subtext}
      </p>

      {locked ? (
        <LockedSwaps count={lockedCount} onUpgrade={handleUpgrade} />
      ) : (
        <div className={`mt-6 grid gap-4 ${isSafe ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
          {shown.map((alt, i) => (
            <SwapCard
              key={alt.id}
              alt={alt}
              index={i}
              onRecheck={onRecheck}
              medicationId={medicationId}
              resultType={verdict}
            />
          ))}
        </div>
      )}

      <p
        data-testid="swap-disclosure"
        className="mt-5 pt-4 border-t border-white/40 text-[11px] text-ink-mute leading-relaxed"
      >
        We may earn a small commission when you purchase through these links at
        no extra cost to you. This helps keep Blendsafe free.
      </p>
    </section>
  );
}
